import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getfloorDatabyStaus } from "../store/actions/floorActions";
import { supabase } from "../supabase/supabaseClient";
import "./building_page.css";
import SEO from "../Components/SEO/SEO";

const TABLE_HEAD = [
  "Unit Number",
  "Gross SQFT",
  "Terrace SQFT",
  "Common Charge",
  "Status",
];

// Define all floors including Parking (0) and Floor 1, then 5-16
const floorOptions = [
  { value: 1, label: "Floor 1" },
  { value: 5, label: "Floor 5" },
  { value: 6, label: "Floor 6" },
  { value: 7, label: "Floor 7" },
  { value: 8, label: "Floor 8" },
  { value: 9, label: "Floor 9" },
  { value: 10, label: "Floor 10" },
  { value: 11, label: "Floor 11" },
  { value: 12, label: "Floor 12" },
  { value: 14, label: "Floor 14" },
  { value: 15, label: "Floor 15" },
  { value: 16, label: "Floor 16" },
];

export default function Availability() {
  const [selectedFloor, setSelectedFloor] = useState(5); // Default to Floor 5
  const [floorAvailability, setFloorAvailability] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const floorData = useSelector((state) => state.floor.list);
  const dispatch = useDispatch();

  // Fetch availability counts for all floors
  useEffect(() => {
    const fetchAvailabilityCounts = async () => {
      const counts = {};
      for (const floor of floorOptions) {
        try {
          const { data, error } = await supabase
            .from("floor")
            .select("Status")
            .eq("floorNumber", floor.value)
            .eq("Status", "Available");

          if (!error) {
            counts[floor.value] = data?.length || 0;
          }
        } catch (error) {
          console.error(
            `Error fetching availability for ${floor.label}:`,
            error
          );
          counts[floor.value] = 0;
        }
      }
      setFloorAvailability(counts);
    };

    fetchAvailabilityCounts();
  }, []);

  useEffect(() => {
    dispatch(getfloorDatabyStaus({ floorNum: selectedFloor, status: "all" }));
  }, [selectedFloor, dispatch]);

  const handleFloorChange = (floorValue) => {
    setSelectedFloor(floorValue);
    setIsDropdownOpen(false);
  };

  const getFloorLabel = (floor) => {
    const available = floorAvailability[floor.value] || 0;
    return `${floor.label} (${available} Available)`;
  };

  const getSelectedFloorLabel = () => {
    const selected = floorOptions.find(
      (floor) => floor.value === selectedFloor
    );
    return selected ? getFloorLabel(selected) : "Select Floor";
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        !event.target.closest(".custom-dropdown-container")
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Sort the floor data by unit number
  const sortedFloorData = floorData?.slice().sort((a, b) => {
    // Convert unit numbers to integers for proper numerical sorting
    const unitA = parseInt(a.unitNumber.replace(/\D/g, ""));
    const unitB = parseInt(b.unitNumber.replace(/\D/g, ""));
    return unitA - unitB;
  });

  const getImagePath = (floorNumber) => {
    if (floorNumber === 1) {
      return "img/resources/new_floor_plans/floor1.png";
    } else {
      // Map floor numbers to button names for image paths
      const floorToButton = {
        5: "Button1",
        6: "Button2",
        7: "Button3",
        8: "Button4",
        9: "Button5",
        10: "Button6",
        11: "Button7",
        12: "Button8",
        14: "Button9",
        15: "Button10",
        16: "Button11",
      };
      const buttonName = floorToButton[floorNumber];
      return buttonName
        ? `img/resources/new_floor_plans/${buttonName}.png`
        : "img/resources/new_floor_plans/floor1.png";
    }
  };

  const getMarginTopClass = (floorNumber) => {
    if (floorNumber === 0 || floorNumber === 1) {
      return "-mt-3 md:-mt-10"; // Less margin for parking and first floor
    }
    return "-mt-16 md:-mt-36"; // Original margins for other floors
  };

  const getFloorDisplayName = (floorNumber) => {
    return `Floor ${floorNumber}`;
  };

  return (
    <>
      <SEO
        title="Available Office Spaces | Union Center - Flushing, NY"
        description="Explore available office spaces at Union Center Flushing. Choose from various unit sizes ranging from 400 to 1600+ sq ft. Modern, sustainable office spaces for sale in prime Downtown Flushing location."
        keywords="Union Center office availability, Flushing commercial space for sale, luxury office unit, NYC office space, Queens commercial property, office space for sale Flushing"
        canonical="https://www.3716union.com/availability"
        ogTitle="Available Office Spaces | Union Center Flushing"
        ogDescription="Modern office spaces for sale in Downtown Flushing. Units from 400-1600+ sq ft available."
        ogImage="https://www.3716union.com/img/resources/Lobby_view3.jpg"
        ogUrl="https://www.3716union.com/availability"
      />
      <div className="flex w-full flex-col bg-[#F0EDE8] py-32">
        <div className="w-full text-[#00000] text-base">
          <section className="w-full md:flex">
            <div className="w-full flex bg-[#F0EDE8] -mt-4 md:-mt-0">
              <div className="w-full max-w-[920px] mx-auto md:pt-[60px] pb-4 md:pb-8 pt-10 text-center px-4">
                <p className="text-[#7e6140] text-4xl md:text-5xl font-normal leading-snug">
                  Availability
                </p>
              </div>
            </div>
          </section>
          <section className="flex-col justify-center w-full md:flex ">
            <div className="w-full max-w-[1000px] mx-auto px-[20px] md:px-[50px] mb-8 md:mb-16">
              <label className="block text-sm font-medium text-black mb-2">
                Select Floor
              </label>
              <div className="custom-dropdown-container relative">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full px-3 py-2.5 text-sm md:px-4 md:py-3 md:text-lg bg-white border-2 border-[#333f48] rounded-md text-black font-medium cursor-pointer focus:outline-none focus:border-[#ad885d] transition-colors duration-200 flex items-center justify-between"
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span className="truncate">{getSelectedFloorLabel()}</span>
                  <svg
                    className={`w-5 h-5 ml-2 transition-transform duration-200 flex-shrink-0 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isDropdownOpen && (
                  <div className="absolute z-50 w-full mt-0 bg-white border-2 border-[#333f48] rounded-md shadow-lg max-h-60 overflow-auto">
                    <ul
                      role="listbox"
                      className="py-1 px-3"
                      aria-label="Floor options"
                    >
                      {floorOptions.map((floor) => (
                        <li
                          key={floor.value}
                          role="option"
                          aria-selected={selectedFloor === floor.value}
                          onClick={() => handleFloorChange(floor.value)}
                          className={`px-3 py-2.5 text-base md:px-4 md:py-2 md:text-base cursor-pointer transition-colors ${
                            selectedFloor === floor.value
                              ? "bg-[#ad885d] text-white"
                              : "text-black hover:bg-gray-100"
                          }`}
                        >
                          {getFloorLabel(floor)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="w-full max-w-[1000px] mx-auto px-[20px] md:px-[50px] pt-[20px] md:pt-[20px] pb-[30px] md:pb-[40px]">
              <div className="max-w-full overflow-x-auto border-1 border-gray-400 rounded-sm md:border-0 md:rounded-none">
                <table className="w-full border-collapse border-solid border border-gray-300 md:border-0">
                  <thead>
                    <tr className="border-b-2 border-black">
                      {TABLE_HEAD.map((head) => (
                        <th
                          key={head}
                          className="py-3 px-3 text-left font-medium text-black border-r border-gray-300 md:border-r-0"
                        >
                          {head}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFloorData && sortedFloorData.length > 0 ? (
                      sortedFloorData.map((item, index) => (
                        <tr key={index} className="border-b border-gray-300">
                          <td className="pt-4 pb-2 px-4 border-r border-gray-300 md:border-r-0">
                            <p className="text-black">{item.unitNumber}</p>
                          </td>
                          <td className="pt-4 pb-2 px-4 border-r border-gray-300 md:border-r-0">
                            <p className="text-black">{item.SQFT}</p>
                          </td>
                          <td className="pt-4 pb-2 px-4 border-r border-gray-300 md:border-r-0">
                            <p className="text-black">
                              {item.terraceSQFT || "0"}
                            </p>
                          </td>
                          <td className="pt-4 pb-2 px-4 border-r border-gray-300 md:border-r-0">
                            <p className="text-black">
                              {item.common_charge || "-"}
                            </p>
                          </td>
                          <td className="pt-4 pb-2 px-4">
                            <p
                              className={`text-md font-medium ${
                                item.Status === "Available"
                                  ? "text-[#10b981]"
                                  : "text-[#ef4444]"
                              }`}
                            >
                              {item.Status}
                            </p>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={TABLE_HEAD.length}
                          className="text-center py-8 text-black"
                        >
                          No units available on this floor
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="w-full relative">
              <div className="w-full md:w-[70%] mx-auto max-w-[950px] overflow-hidden relative">
                <p className="absolute top-5 md:top-12 left-1/2 transform -translate-x-1/2 z-10 text-lg md:text-2xl font-bold text-[#ad885d] bg-none px-4 py-2 rounded-md">
                  {getFloorDisplayName(selectedFloor)}
                </p>
                <img
                  src={getImagePath(selectedFloor)}
                  className={`w-full ${getMarginTopClass(
                    selectedFloor
                  )} -mb-5 rounded-lg md:rounded-xl`}
                  alt={`Floor plan for ${getFloorDisplayName(selectedFloor)}`}
                  style={{
                    clipPath: "inset(20px 0)",
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
